import { useState } from 'react';
import { UserAlertFlags } from '@presto-assistant/api_types';

import { getFullName } from 'utils';
import { useIsOpen } from 'hooks/useIsOpen';
import { useRemoveUserAlertFlag, useSetUserAlertFlag } from 'gql/mutations/alertFlags';
import {
  GET_USERS_BY_ALERT_FLAGS,
  getUsersByFraudAlertFlag,
} from 'gql/queries/alertFlags';
import DataGridContainer from 'components/shared/TableDataGrid/DataGridContainer';
import MarkAsFraudDialog from 'components/shared/MarkAsFraudDialog';
import TableDataGrid from 'components/shared/TableDataGrid';

import { useColumns } from './hooks';
import type { OrganizationDirectorRow } from './types';

// Local Typings
interface Props {
  directors: GQL.IOrganization['directors'];
}

// Component Definition
const OrganizationDirectorsTable = ({ directors }: Props): JSX.Element => {
  const [selectedUser, setSelectedUser] = useState<OrganizationDirectorRow | null>(null);

  const { data } = getUsersByFraudAlertFlag();

  const {
    isOpen: isFlagUserConfirmationDialogOpen,
    toggleIsOpen: toggleFlagUserConfirmationDialogIsOpen,
  } = useIsOpen();

  const [
    handleRemoveUserAlertFlag,
    {
      error: removeError,
      loading: isRemovingFlag,
    },
  ] = useRemoveUserAlertFlag({
    onCompleted: toggleFlagUserConfirmationDialogIsOpen,
    refetchQueries: [{ query: GET_USERS_BY_ALERT_FLAGS }],
  });

  const [
    handleSetUserAlertFlag,
    {
      error: addError,
      loading: isAddingFlag,
    },
  ] = useSetUserAlertFlag({
    onCompleted: toggleFlagUserConfirmationDialogIsOpen,
    refetchQueries: [{ query: GET_USERS_BY_ALERT_FLAGS }],
  });

  const isLoading = isRemovingFlag || isAddingFlag;

  const isCurrentUserMarkedAsFraud = data?.usersByAlertFlag.some(
    (user) => user.id === selectedUser?.authUserId,
  );

  const handleFraudStatusUpdate = async () => {
    if (selectedUser?.authUserId && isCurrentUserMarkedAsFraud) {
      await handleRemoveUserAlertFlag({
        variables: { input: { userId: selectedUser.authUserId } },
      });
    } else if (selectedUser?.authUserId) {
      await handleSetUserAlertFlag({
        variables: {
          input: {
            userAlertFlagId: UserAlertFlags.Fraud.toString(),
            userId: selectedUser.authUserId,
          },
        },
      });
    }
  };

  const handleFraudActionClick = (userData: OrganizationDirectorRow) => {
    setSelectedUser(userData);
    toggleFlagUserConfirmationDialogIsOpen();
  };

  const columns = useColumns({
    fraudUserIds: data?.usersByAlertFlag?.map((user) => user.id) ?? [],
    onFraudActionClick: handleFraudActionClick,
  });

  return (
    <>
      <DataGridContainer>
        <TableDataGrid
          columns={columns}
          rows={directors ?? []}
          withSearch
        />
      </DataGridContainer>

      <MarkAsFraudDialog
        addError={addError}
        isAdmin={false}
        isCurrentUserMarkedAsFraud={isCurrentUserMarkedAsFraud}
        isLoading={isLoading}
        isOpen={isFlagUserConfirmationDialogOpen}
        onDialogClose={toggleFlagUserConfirmationDialogIsOpen}
        onUpdateFraudStatus={handleFraudStatusUpdate}
        removeError={removeError}
        userFullName={selectedUser ? getFullName(selectedUser) : ''}
      />
    </>
  );
};

export default OrganizationDirectorsTable;
